/**
 * Help & User Guide Page
 * Comprehensive documentation for CampusOS features
 * Accessible at /app/help
 */

import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader, BadgePill } from '@/components/campus';
import {
  HelpCircle,
  Search,
  BookOpen,
  Calendar,
  Users,
  DollarSign,
  FileText,
  Shield,
  QrCode,
  Megaphone,
  UserPlus,
  BarChart3,
  GraduationCap,
  Settings,
  Zap,
  Mail,
  CheckCircle,
  ExternalLink,
  MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ==================== HELP CONTENT DATA ====================

const QUICK_START_STEPS = [
  {
    step: 1,
    title: 'Set Up Your Organization',
    description: 'Configure your chapter profile, logo, and social links in Settings > General.',
    icon: Settings,
  },
  {
    step: 2,
    title: 'Invite Members',
    description: 'Go to Members and invite your chapter members via email. Assign appropriate roles.',
    icon: Users,
  },
  {
    step: 3,
    title: 'Create Your First Event',
    description: 'Navigate to Events and create an event with details, capacity, and ticketing options.',
    icon: Calendar,
  },
  {
    step: 4,
    title: 'Set Up Dues',
    description: 'Go to Dues and create dues with amounts and due dates. Members will be notified.',
    icon: DollarSign,
  },
  {
    step: 5,
    title: 'Configure Permissions',
    description: 'In Settings > Permissions, customize what each role can access.',
    icon: Shield,
  },
];

const FEATURE_GUIDES = {
  events: {
    title: 'Event Management',
    icon: Calendar,
    description: 'Create, manage, and track events for your organization.',
    topics: [
      {
        title: 'Creating an Event',
        content: `
1. Navigate to **Events** from the sidebar
2. Click **Create Event** button
3. Fill in event details:
   - Event name and description
   - Date, start time, and end time
   - Location
   - Capacity
   - Visibility (public/private/members-only)
4. Configure ticketing if needed:
   - Enable ticketing and set price
   - Configure age verification requirements
5. Click **Create** to save

**Tip:** Draft events are not visible to members until published.
        `,
      },
      {
        title: 'Managing Guest Lists',
        content: `
- View all attendees by clicking on an event
- See check-in status in real-time
- Export guest list to CSV for records
- Manually add guests if needed

**Guest Status Types:**
- **Pending**: Awaiting approval (for invite-only events)
- **Approved**: Can attend and receive QR code
- **Checked In**: Has entered the event
- **Denied**: Entry was denied (with reason logged)
        `,
      },
      {
        title: 'QR Code Check-In',
        content: `
Members receive a unique QR code after RSVPing. Door staff can scan these codes using:

1. **Scanner Page**: Full-featured scanning interface
2. **Door Staff Mode**: Simplified view for assigned door staff

**Check-In Flow:**
1. Guest shows QR code
2. Staff scans code
3. System validates and shows guest info
4. Staff confirms or denies entry
        `,
      },
      {
        title: 'Door Staff Assignment',
        content: `
Assign door staff to work specific events:

1. Go to event details
2. Click **Manage Door Staff**
3. Select users with door_staff role
4. Click **Assign**

Door staff assignments automatically expire when the event ends.
        `,
      },
    ],
  },
  members: {
    title: 'Member Management',
    icon: Users,
    description: 'Invite, manage, and organize your organization members.',
    topics: [
      {
        title: 'Member Roles',
        content: `
**Available Roles:**

| Role | Description | Access Level |
|------|-------------|--------------|
| **Owner** | Chapter president/founder | Full access, cannot be removed |
| **Admin** | Executive board members | Full access except billing |
| **Event Manager** | Social chairs, event planners | Events and attendance |
| **Door Staff** | Check-in personnel | Scanner and guest lists only |
| **Member** | Regular members | Basic access, events, dues |

**Officer Titles:**
Assign titles like President, Treasurer, etc. in Settings > General.
- Treasurer title unlocks dues creation
- Risk Manager title unlocks document management
        `,
      },
      {
        title: 'Inviting Members',
        content: `
1. Go to **Members** page
2. Click **Invite Member**
3. Enter email address
4. Select role
5. Click **Send Invite**

Invited users receive an email with a signup link. Once they create an account, they'll automatically be added to your organization.
        `,
      },
      {
        title: 'Managing Alumni',
        content: `
When members graduate:
1. Change their status to "Alumni" in member settings
2. They'll appear in the Alumni Directory
3. Alumni can update their own profile (employer, city, LinkedIn)
4. Alumni retain limited access to events and announcements
        `,
      },
    ],
  },
  dues: {
    title: 'Dues Management',
    icon: DollarSign,
    description: 'Create, track, and collect membership dues.',
    topics: [
      {
        title: 'Creating Dues',
        content: `
1. Go to **Dues** page
2. Click **Create Due**
3. Set:
   - Due name (e.g., "Spring 2026 Dues")
   - Amount
   - Due date
   - Applicable member statuses
4. Click **Create**

The system automatically creates individual dues for all applicable members.
        `,
      },
      {
        title: 'Payment Processing',
        content: `
Members can pay dues through:
- **Stripe Checkout**: Secure online payment
- **Manual marking**: Treasurers can mark dues as paid for cash/check

**Payment Statuses:**
- Outstanding: Not yet paid
- Paid: Payment received
- Waived: Excused from payment

Treasurers can export payment data to CSV for records.
        `,
      },
      {
        title: 'Reminders',
        content: `
Configure automatic reminders in **Settings > Notifications**:
- 7 days before due date
- 1 day before due date
- On due date

You can also manually send reminders from the dues detail view.
        `,
      },
    ],
  },
  documents: {
    title: 'Document Vault',
    icon: FileText,
    description: 'Upload documents and collect electronic signatures.',
    topics: [
      {
        title: 'Uploading Documents',
        content: `
1. Go to **Documents**
2. Click **Upload Document**
3. Select file (PDF, Image, or text)
4. Choose document type
5. Enable **Requires Signature** if needed
6. Select which member statuses should sign
7. Upload

Documents are stored securely and accessible to authorized members.
        `,
      },
      {
        title: 'Electronic Signatures',
        content: `
When a document requires signature:

**For Members:**
1. Open the document
2. Review content
3. Sign using:
   - **Draw**: Use mouse/touch to sign
   - **Type**: Enter name in cursive font
4. Submit signature

**For Admins:**
- Track signature completion percentage
- See who has/hasn't signed
- Send reminders to pending signers
        `,
      },
      {
        title: 'Document Templates',
        content: `
Create reusable templates at **Documents > Templates**:

1. Click **New Template**
2. Enter template name
3. Add content with placeholders:
   - \`{{member_name}}\` - Auto-fills member's name
   - \`{{organization_name}}\` - Your chapter name
   - \`{{date}}\` - Current date
4. Save template

When generating documents, placeholders are automatically filled.
        `,
      },
    ],
  },
  announcements: {
    title: 'Announcements',
    icon: Megaphone,
    description: 'Post updates and communicate with members.',
    topics: [
      {
        title: 'Creating Announcements',
        content: `
1. Go to **Announcements**
2. Click **New Announcement**
3. Enter title and content
4. Select audience:
   - All Members
   - Active Members
   - Officers
   - Pledges
   - Alumni
5. Optionally add attachment
6. Post

Members will see announcements in their feed and optionally receive email notifications.
        `,
      },
      {
        title: 'Read Tracking',
        content: `
Admins can see:
- Total read count
- Which members have read
- Read percentage

Useful for ensuring important information reaches everyone.
        `,
      },
    ],
  },
  recruitment: {
    title: 'Rush Pipeline',
    icon: UserPlus,
    description: 'Manage potential new members through the recruitment process.',
    topics: [
      {
        title: 'Pipeline Stages',
        content: `
Track PNMs through these stages:

1. **Prospect** - Initial contact
2. **Contacted** - Reached out
3. **Rushed** - Attended events
4. **Bid Extended** - Offered membership
5. **Bid Accepted** - Accepted offer
6. **Pledged** - In pledge program
7. **Initiated** - Full member

Drag and drop cards between stages to update status.
        `,
      },
      {
        title: 'Adding PNMs',
        content: `
1. Click **Add PNM**
2. Enter:
   - Name and email
   - School/university
   - Notes (interests, mutual connections, etc.)
3. Save

The PNM will appear in the Prospect column.
        `,
      },
      {
        title: 'Converting to Members',
        content: `
When a PNM reaches Initiated stage:
1. Open their profile
2. Click **Convert to Member**
3. An invitation email will be sent
4. Once accepted, they become a full member

The conversion is tracked for analytics.
        `,
      },
    ],
  },
  analytics: {
    title: 'Analytics',
    icon: BarChart3,
    description: 'Track organization performance and engagement.',
    topics: [
      {
        title: 'Dashboard Stats',
        content: `
**Six Key Metrics:**
1. **Active Members** - Count of active organization members
2. **Dues Collection Rate** - % of dues paid vs. outstanding
3. **Event Attendance Rate** - % checked in at events (90 days)
4. **Document Signature Rate** - % of required signatures collected
5. **Rush Conversion Rate** - % of PNMs converted to members
6. **Wallet Balance** - Total sponsor revenue

Each metric shows a 30-day trend (up/down arrow).
        `,
      },
      {
        title: 'Charts',
        content: `
**Event Attendance Chart:**
Bar chart showing attendance rate for recent events.

**Dues Collection Chart:**
Line chart showing monthly collection trends.

Use these to identify patterns and improve engagement.
        `,
      },
    ],
  },
  settings: {
    title: 'Settings',
    icon: Settings,
    description: 'Configure organization settings, permissions, and notifications.',
    topics: [
      {
        title: 'General Settings',
        content: `
**Organization Profile:**
- Chapter name
- Logo (upload image)
- Description
- Social links (website, Instagram, Twitter)

**Officer Titles:**
Assign executive positions to admins. Special titles unlock features:
- Treasurer → Dues creation
- Risk Manager → Document management

**Age Verification:**
Configure PassportReader API keys for ID verification at events.
        `,
      },
      {
        title: 'Permission Settings',
        content: `
Control what each role can access:

**Categories:**
- Events (view, create, manage)
- Org Management (members, recruitment)
- Finance (dues, payments)
- Communications (documents, announcements)

**Locked Permissions:**
- Owner always has full access
- Door staff cannot access dues or documents

Changes take effect immediately.
        `,
      },
      {
        title: 'Notification Settings',
        content: `
Configure automated emails:
- Dues reminders (7 days, 1 day, due date)
- Document signature requests
- Announcement emails
- Event reminders

Toggle each notification type on/off.
        `,
      },
    ],
  },
};

const FAQ_ITEMS = [
  {
    question: 'How do I change a member\'s role?',
    answer: 'Go to Members, find the member, click the three-dot menu, and select "Change Role". Only owners can change roles.',
  },
  {
    question: 'Can members pay dues with cash or check?',
    answer: 'Yes! Treasurers can manually mark dues as paid in the dues detail view. Select the member and click "Mark as Paid".',
  },
  {
    question: 'How do I remove a member?',
    answer: 'Go to Members, find the member, click the menu, and select "Remove". This will revoke their access. For graduates, change status to Alumni instead.',
  },
  {
    question: 'Why can\'t door staff see the dues page?',
    answer: 'Door staff have limited access for security. They can only access the scanner and guest lists. This is a locked permission that cannot be changed.',
  },
  {
    question: 'How do I export data?',
    answer: 'Most pages have an Export or Download button. You can export guest lists, dues records, and member lists to CSV.',
  },
  {
    question: 'Can I undo a check-in?',
    answer: 'Currently, check-ins cannot be undone through the UI. Contact support if you need to reverse a check-in.',
  },
  {
    question: 'How do I set up age verification?',
    answer: 'Go to Settings > General > Age Verification. Enter your PassportReader API keys and click Test Connection to verify.',
  },
  {
    question: 'Why aren\'t members receiving emails?',
    answer: 'Check Settings > Notifications to ensure the relevant notification type is enabled. Also verify the member\'s email is correct.',
  },
];

// ==================== COMPONENTS ====================

const QuickStartCard = ({ step }) => (
  <div className="flex gap-4 p-4 bg-graphite/50 rounded-xl border border-white/[0.06]">
    <div className="w-10 h-10 rounded-full bg-electric-ice-blue flex items-center justify-center flex-shrink-0">
      <span className="text-white font-bold">{step.step}</span>
    </div>
    <div>
      <h3 className="font-medium text-cloud-white">{step.title}</h3>
      <p className="text-sm text-soft-silver mt-1">{step.description}</p>
    </div>
  </div>
);

const FeatureSection = ({ feature }) => {
  const Icon = feature.icon;
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-electric-ice-blue" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-cloud-white">{feature.title}</h2>
          <p className="text-sm text-soft-silver">{feature.description}</p>
        </div>
      </div>

      <Accordion type="single" collapsible className="space-y-2">
        {feature.topics.map((topic, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="bg-graphite/50 rounded-xl border border-white/[0.06] px-4"
          >
            <AccordionTrigger className="text-cloud-white hover:text-electric-ice-blue py-4">
              {topic.title}
            </AccordionTrigger>
            <AccordionContent className="text-soft-silver pb-4">
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-soft-silver leading-relaxed">
                  {topic.content.trim()}
                </pre>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

// ==================== MAIN PAGE ====================

export const Help = () => {
  const { organization } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('quickstart');

  // Filter FAQ by search
  const filteredFAQ = FAQ_ITEMS.filter(
    (item) =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="help-page">
      <PageHeader
        title="Help & User Guide"
        subtitle="Learn how to use CampusOS effectively"
        icon={HelpCircle}
      />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
        <Input
          placeholder="Search help topics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-graphite border-white/[0.08] text-cloud-white"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-graphite/50 border border-white/[0.06] p-1 rounded-lg flex-wrap h-auto">
          <TabsTrigger value="quickstart" className="data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue">
            <Zap className="w-4 h-4 mr-2" />
            Quick Start
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue">
            <BookOpen className="w-4 h-4 mr-2" />
            Features
          </TabsTrigger>
          <TabsTrigger value="faq" className="data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue">
            <MessageSquare className="w-4 h-4 mr-2" />
            FAQ
          </TabsTrigger>
        </TabsList>

        {/* Quick Start */}
        <TabsContent value="quickstart" className="mt-6 space-y-4">
          <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
            <h3 className="font-medium text-cool-mint">Welcome to CampusOS!</h3>
            <p className="text-sm text-soft-silver mt-1">
              Follow these steps to get your organization up and running.
            </p>
          </div>

          <div className="space-y-3">
            {QUICK_START_STEPS.map((step) => (
              <QuickStartCard key={step.step} step={step} />
            ))}
          </div>

          <div className="p-4 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/20 mt-6">
            <h3 className="font-medium text-electric-ice-blue">Need more help?</h3>
            <p className="text-sm text-soft-silver mt-1">
              Explore the Features tab for detailed guides on each feature, or check the FAQ for common questions.
            </p>
          </div>
        </TabsContent>

        {/* Features */}
        <TabsContent value="features" className="mt-6 space-y-8">
          {Object.entries(FEATURE_GUIDES).map(([key, feature]) => (
            <FeatureSection key={key} feature={feature} />
          ))}
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq" className="mt-6">
          <Accordion type="single" collapsible className="space-y-2">
            {(searchQuery ? filteredFAQ : FAQ_ITEMS).map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-graphite/50 rounded-xl border border-white/[0.06] px-4"
              >
                <AccordionTrigger className="text-cloud-white hover:text-electric-ice-blue py-4 text-left">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-soft-silver pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {searchQuery && filteredFAQ.length === 0 && (
            <div className="text-center py-8 text-muted-text">
              <p>No results found for "{searchQuery}"</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Contact Support */}
      <div className="p-6 bg-graphite/50 rounded-2xl border border-white/[0.06] mt-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <Mail className="w-6 h-6 text-electric-ice-blue" />
          </div>
          <div>
            <h3 className="font-semibold text-cloud-white">Still need help?</h3>
            <p className="text-sm text-soft-silver mt-1">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Button
              variant="outline"
              className="mt-4 border-electric-ice-blue/30 text-electric-ice-blue"
              onClick={() => window.location.href = 'mailto:support@campassed.com'}
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
